import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { getTodaySessions, generateSessionVariants } from "../../lib/api";
import { getMatiereInfo, getSessionTypeInfo, JournalStatusBadge } from "./constants";
import {
  Calendar,
  Clock,
  FileText,
  AlertTriangle,
  Check,
  Sparkles,
  Plus,
  ChevronRight,
  Loader2,
  Target,
  Users,
  Star,
  Sun,
} from "lucide-react";
import { toast } from "sonner";

function TodaySessionItem({ session, onOpenResource, onCreateResource, onGenerateVariants }) {
  const matiereInfo = getMatiereInfo(session.matiere);
  const sessionTypeInfo = getSessionTypeInfo(session.session_type);
  const Icon = matiereInfo?.icon;
  const hasResource = !!session.resource_id;
  const missingVariants = session.missing_variants;
  const [generating, setGenerating] = useState(false);

  const handleGenerateVariants = async (e) => {
    e.stopPropagation();
    setGenerating(true);
    try {
      const { data } = await generateSessionVariants(session.id);
      toast.success(`${data.variants_generated} variante(s) générée(s)`);
      onGenerateVariants();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <TooltipProvider>
      <div
        data-testid={`today-session-${session.id}`}
        className="flex items-center gap-3 p-3 rounded-xl border bg-white hover:shadow-sm transition-all"
        style={{ borderColor: `${matiereInfo?.color}30` }}
      >
        {/* Time */}
        <div className="text-center min-w-[50px]">
          <span className="text-sm font-bold text-slate-700">{session.start_time}</span>
        </div>

        {/* Matiere icon */}
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${matiereInfo?.color}15` }}
        >
          {Icon && <Icon className="w-5 h-5" style={{ color: matiereInfo?.color }} />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-semibold text-sm text-slate-800 truncate">{session.title}</span>
            <JournalStatusBadge status={session.status} className="text-[10px]" />
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${sessionTypeInfo?.color}`}>
              {sessionTypeInfo?.label}
            </Badge>
            {session.niveau && (
              <span className="uppercase">{session.niveau}</span>
            )}
            {session.main_skill && (
              <span className="flex items-center gap-0.5 truncate max-w-[120px]">
                <Star className="w-2.5 h-2.5 text-yellow-500" />
                {session.main_skill.label}
              </span>
            )}
          </div>
        </div>

        {/* Resource status & actions */}
        <div className="flex items-center gap-1">
          {hasResource ? (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenResource(session.resource_id);
                    }}
                  >
                    <FileText className="w-4 h-4 text-indigo-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Ouvrir la ressource</TooltipContent>
              </Tooltip>
              
              {missingVariants && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-amber-500"
                      onClick={handleGenerateVariants}
                      disabled={generating}
                    >
                      {generating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <AlertTriangle className="w-4 h-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {session.variants_needed - (session.variants_count || 0)} variante(s) manquante(s)
                  </TooltipContent>
                </Tooltip>
              )}
            </>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-green-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateResource(session.id);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Fiche
                </Button>
              </TooltipTrigger>
              <TooltipContent>Créer une fiche pour cette séance</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

export function TodayBlock() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchTodayData = async () => {
    try {
      const { data: todayData } = await getTodaySessions();
      setData(todayData);
      setError(null);
    } catch (err) {
      console.error("Error fetching today sessions:", err);
      setError("Impossible de charger les séances");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayData();
  }, []);

  const handleOpenResource = (resourceId) => {
    navigate(`/create/${resourceId}`);
  };

  const handleCreateResource = async (sessionId) => {
    try {
      const { createResourceFromSession } = await import("../../lib/api");
      const { data } = await createResourceFromSession(sessionId);
      toast.success("Fiche créée !");
      navigate(`/create/${data.id}`);
    } catch (err) {
      toast.error("Erreur lors de la création de la fiche");
    }
  };

  const formatDateFr = (dateStr) => {
    if (!dateStr) {
      const today = new Date();
      const options = { weekday: 'long', day: 'numeric', month: 'long' };
      return today.toLocaleDateString('fr-FR', options);
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      const today = new Date();
      const options = { weekday: 'long', day: 'numeric', month: 'long' };
      return today.toLocaleDateString('fr-FR', options);
    }
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return date.toLocaleDateString('fr-FR', options);
  };

  if (loading) {
    return (
      <Card className="mb-6 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
        <CardHeader className="pb-2">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null; // Silently fail if API error
  }

  const sessions = data?.sessions || [];
  const summary = data?.summary || {};
  const hasAlerts = summary.missing_variants > 0 || summary.without_resources > 0;

  return (
    <Card 
      data-testid="today-block"
      className="mb-6 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sun className="w-5 h-5 text-amber-500" />
            <span>Aujourd'hui</span>
            <span className="text-slate-500 font-normal text-sm">
              — {formatDateFr(data?.date)}
            </span>
          </CardTitle>
          
          {sessions.length > 0 && (
            <div className="flex items-center gap-2">
              {/* Summary badges */}
              <Badge variant="secondary" className="bg-white">
                {summary.total} séance{summary.total > 1 ? 's' : ''}
              </Badge>
              {summary.status?.fait > 0 && (
                <Badge className="bg-green-100 text-green-700">
                  <Check className="w-3 h-3 mr-1" />
                  {summary.status.fait} fait{summary.status.fait > 1 ? 's' : ''}
                </Badge>
              )}
              {hasAlerts && (
                <Badge className="bg-amber-100 text-amber-700">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {summary.missing_variants > 0 && `${summary.missing_variants} variante${summary.missing_variants > 1 ? 's' : ''}`}
                  {summary.missing_variants > 0 && summary.without_resources > 0 && ' · '}
                  {summary.without_resources > 0 && `${summary.without_resources} sans fiche`}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent>
        {sessions.length === 0 ? (
          <div className="text-center py-6">
            <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm mb-3">Aucune séance prévue aujourd'hui</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/teacher-hub?tab=journal")}
              className="text-amber-700 border-amber-300 hover:bg-amber-100"
            >
              <Plus className="w-4 h-4 mr-1" />
              Planifier une séance
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.slice(0, 5).map((session) => (
              <TodaySessionItem
                key={session.id}
                session={session}
                onOpenResource={handleOpenResource}
                onCreateResource={handleCreateResource}
                onGenerateVariants={fetchTodayData}
              />
            ))}
            
            {sessions.length > 5 && (
              <Button
                variant="ghost"
                className="w-full text-amber-700 hover:bg-amber-100 mt-2"
                onClick={() => navigate("/teacher-hub?tab=journal")}
              >
                Voir toutes les séances ({sessions.length})
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
